import React, { FC } from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"

import { ISeoProps } from "gatsby-theme-nurofen/src/components/common/Seo/models"
import ExternalScripts from "gatsby-theme-nurofen/src/components/common/Seo/ExternalScripts"
import Fonts from "gatsby-theme-nurofen/src/components/common/Seo/constants"

const Seo: FC<ISeoProps> = ({
  siteSettings,
  description,
  lang,
  keywords,
  title,
  externalHreflangs,
  ogImage,
}) => {
  const siteTitle = siteSettings.siteName || ""
  const titleTemplate = title === siteTitle ? "%s" : `%s | ${siteTitle}`
  const siteLang = lang || siteSettings.lang
  const siteDir = siteSettings?.dir?.[0] || "ltr"

  const ogImageUrl =
    externalHreflangs?.find(item => item.key.toLowerCase() === "ogimage")
      ?.value || ""

  const filteredExternalHreflangs = externalHreflangs?.filter(
    item => item.key !== "ogImage"
  )

  const meta = [
    {
      name: "robots",
      content:
        process.env.GATSBY_ACTIVE_ENV === "production"
          ? "index, follow"
          : "noindex, nofollow",
    },
    {
      name: "description",
      content: description,
    },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:url",
      content: useLocation().href,
    },
    {
      property: "og:image",
      content: ogImageUrl || "",
    },
    {
      name: "twitter:card",
      content: "summary",
    },
    {
      name: "twitter:creator",
      content: siteTitle,
    },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    ...(ogImage
      ? [
          {
            property: "og:image",
            content: ogImage,
          },
        ]
      : []),
    ...(keywords?.length
      ? [
          {
            name: "keywords",
            content: keywords.join(", "),
          },
        ]
      : []),
  ]

  return (
    <Helmet
      htmlAttributes={{ lang: siteLang, dir: siteDir }}
      {...{ title, titleTemplate, meta }}
    >
      {Fonts?.map(font => {
        return (
          <link
            key={font}
            rel="preload"
            as="font"
            href={font}
            type="font/woff"
            crossOrigin="anonymous"
          />
        )
      })}
      {filteredExternalHreflangs?.map(link => {
        return <link rel="alternate" href={link.value} hrefLang={link.key} />
      })}
      <ExternalScripts />
    </Helmet>
  )
}

export default Seo
